import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';

import { Skeleton } from '@repo/ui/components/ui/skeleton';
import { usePhotographer } from '@/state/use-photographer';
import { getPhotoVogueService, type Photo } from '@/services/photovogue';
import { PhotographerDetailView } from '@/components/photovogue/photographer-detail/component';

export const PhotographerDetail = () => {
  const { photographerId } = useParams<{ photographerId: string }>();
  const {
    data: photographer,
    loading,
    error,
    updating,
    fetch,
    moderate,
    updateSpotlight,
    reset,
  } = usePhotographer();
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [photosLoading, setPhotosLoading] = useState(true);

  useEffect(() => {
    if (!photographerId) return;
    fetch(photographerId);
    return () => reset();
  }, [photographerId, fetch, reset]);

  useEffect(() => {
    if (!photographerId) return undefined;
    let active = true;
    setPhotosLoading(true);
    getPhotoVogueService()
      .getPhotos({
        filters: { photographerId },
        pagination: { page: 0, limit: 48, skip: 0 },
        sort: { field: 'createdAt', direction: 'desc' },
      })
      .then((response) => {
        if (active) setPhotos(response.data);
      })
      .finally(() => {
        if (active) setPhotosLoading(false);
      });
    return () => {
      active = false;
    };
  }, [photographerId]);

  return (
    <section>
      <Link
        to="/photovogue/admin/photographers"
        className="mb-4 inline-block text-sm text-cn-muted-foreground underline-offset-4 hover:underline"
      >
        ← All photographers
      </Link>

      {error && !photographer ? (
        <div role="alert" className="rounded-lg border p-6">
          <p>Unable to load photographer details.</p>
          <button
            type="button"
            className="mt-2 underline"
            onClick={() => photographerId && fetch(photographerId)}
          >
            Retry
          </button>
        </div>
      ) : loading || !photographer ? (
        <div className="space-y-6">
          <Skeleton className="h-28 w-full rounded-lg" />
          <Skeleton className="h-64 w-full rounded-lg" />
        </div>
      ) : (
        <PhotographerDetailView
          photographer={photographer}
          photos={photos}
          photosLoading={photosLoading}
          updating={updating}
          onStateChange={moderate}
          onSpotlightChange={updateSpotlight}
        />
      )}
    </section>
  );
};

export default PhotographerDetail;
